import { api } from './client'

// --- Interfaces ---

export interface DbRecommendation {
  id: number
  db_identifier: string
  type: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  status: 'open' | 'applied' | 'dismissed'
  title: string
  description: string
  estimated_impact: string | null
  sql_to_apply?: string | null
  created_at: string
  updated_at: string
}

export interface DbRecommendationListResponse {
  items: DbRecommendation[]
  count: number
}

export interface ApplyRecommendationResponse {
  status: 'applied' | 'approval_pending'
  maintenance_request_id: number | null
}

export interface DismissRecommendationRequest {
  reason: string
}

export interface CapacityForecastMetric {
  current_pct: number | null
  growth_rate_per_day: number | null
  days_until_threshold: number | null
}

export interface CapacityForecast {
  storage: CapacityForecastMetric
  connections: CapacityForecastMetric
  cpu: CapacityForecastMetric
}

export interface MetricPoint {
  ts: string
  value: number
}

export interface MetricsHistoryResponse {
  metric: string
  points: MetricPoint[]
}

export interface RdsInstance {
  db_identifier: string
  engine: string
  engine_version: string
  instance_class: string
  status: string
  endpoint: string
  port: number
  multi_az: boolean
  storage_gb: number
  region?: string
  /**
   * `null`/ausente = NAO FOI POSSIVEL DETERMINAR, e nao "desligado".
   *
   * Vem de `describe_db_instances`, que a plataforma sempre pode ler. E o unico sinal
   * autoritativo sobre o estado do Performance Insights — antes disto a tela inferia o estado de
   * um erro numa chamada `pi:`, que a AWS tambem devolve quando falta permissao.
   */
  performance_insights_enabled?: boolean | null
}

export interface RdsInstanceListResponse {
  items: RdsInstance[]
  count: number
}

export interface RdsLiveMetrics {
  cpu_pct: number
  connections: number
  free_storage_gb: number
  read_latency_ms: number
  write_latency_ms: number
}

export interface RdsMetricsResponse {
  live: RdsLiveMetrics
  snapshots: Array<{ metric_name: string; value: number; collected_at: string }>
  partial?: boolean
}

export interface RdsInstanceStatus {
  status: string
  health: 'healthy' | 'warning' | 'critical'
  cpu_pct: number
  connections: number
}

export interface SlowQuery {
  query_text: string
  /**
   * `db.sql_tokenized.id` — a chave para pedir o texto INTEIRO (`getQueryStatement`). `null`
   * quando o Performance Insights nao devolveu o id: a linha continua valendo, so nao da para
   * pedir o texto completo dela.
   */
  query_id: string | null
  /**
   * A AWS cortou este texto. O `DescribeDimensionKeys` limita o statement a **500** (medido em
   * 24/08/2026 no `goandroid-prd`: 2 das 15 chaves cortadas no meio do identificador), e quem
   * decide isso e o backend — e ele que conhece o teto da API, e ele olha caractere E byte.
   */
  query_text_truncado: boolean
  avg_load: number
  /**
   * Chamadas na janela, DERIVADO de `calls_per_sec`. `null` quando o Performance Insights nao
   * devolveu a metrica — o que e caso normal e frequente (medido em 18/08/2026: 3 de 15 linhas
   * no `goandroid-prd`, 14 de 15 no `safecheck-prd`).
   *
   * `null` e nao `number`: ate 18/08 o backend devolvia `0` na ausencia, e a tela afirmava
   * "0 chamadas" ao lado de 10 segundos de tempo total. Ausencia se mostra como "—".
   */
  calls: number | null
  /** A taxa como a API devolveu, sem transformacao — torna a derivacao de `calls` auditavel. */
  calls_per_sec: number | null
  avg_latency_ms: number | null
  total_time_ms: number
}

/**
 * Resposta da rota de texto completo. `status` e o campo da AWS, repassado: `PROCESSING` e
 * *tente de novo*, `UNAVAILABLE` e *nao existe*. Achatar os dois em "nao deu" faria a tela
 * mandar o usuario esperar por algo que nao vem.
 */
export interface QueryStatement {
  available: boolean
  statement?: string
  /**
   * O `GetDimensionKeyDetails` **tambem corta** — em 4095 caracteres. Medido em producao em
   * 25/08/2026 no `goandroid-prd`: das 3 queries truncadas na listagem, duas voltaram com
   * exatamente 4095, terminando no meio da clausula (`..."cn_code_product" AND`). Sem esta
   * marca a tela diz "Texto completo" sobre texto cortado.
   */
  statement_truncado?: boolean
  status?: 'AVAILABLE' | 'PROCESSING' | 'UNAVAILABLE' | string
  motivo?: string
  error?: string
}

export interface SlowQueriesResponse {
  items: SlowQuery[]
  count: number
  /** Janela ALINHADA que o PI usou, em segundos. E o que permite rotular "Chamadas (24h)". */
  janela_segundos?: number
  error?: string
}

/**
 * Marca de "Performance Insights desabilitado" que o resto do frontend conhece. Mantida como
 * constante porque a PerformanceTab compara por igualdade para escolher entre o alerta amarelo
 * (que diz o que fazer) e a mensagem generica de falha.
 */
export const PI_DESABILITADO = 'performance_insights_not_enabled'

/** Reconhece a frase que o roteador usa para PI desabilitado, nas duas redacoes que ele tem. */
function ePiDesabilitado(mensagem: unknown): boolean {
  return typeof mensagem === 'string' && /performance insights.*(nao|não)\s+(esta|está)?\s*habilitado/i.test(mensagem)
}

/**
 * Uma resposta, duas formas — e o frontend so conhecia uma.
 *
 * Medido em producao em 2026-08-14 (tenant COMMERCE-DEV):
 *
 *   HTTP 200 {"available":false,"message":"Performance Insights nao habilitado.","queries":[]}
 *
 * O servico (db_insights_service) trata ClientError e devolve {error: PI_DESABILITADO}. Mas
 * `_resolve_dbi_resource_id()` roda ANTES daquele try; quando e ele que estoura, a excecao escapa
 * para o `except Exception` do roteador, que devolve {available, message, queries}.
 *
 * O sintoma nao era so o alerta faltando: **query lenta em `queries` nunca chegava a tabela**,
 * porque a tela le `items`. A aba dizia "nenhuma query lenta" mesmo com dado.
 *
 * Normalizar aqui, na fronteira, e nao em cada tela: assim existe um lugar so para acertar, e a
 * tela continua lendo o contrato declarado.
 */
export function normalizarSlowQueries(bruto: unknown): SlowQueriesResponse {
  const o = (bruto ?? {}) as Record<string, unknown>
  if (typeof bruto !== 'object' || bruto === null) return { items: [], count: 0 }

  const lista = (Array.isArray(o.items) ? o.items : Array.isArray(o.queries) ? o.queries : []) as SlowQuery[]
  const contagem = typeof o.count === 'number' ? o.count : lista.length

  let erro = typeof o.error === 'string' ? o.error : undefined
  if (!erro && o.available === false) {
    // Nem toda indisponibilidade e falta de PI: marcar tudo como PI-desabilitado mandaria o
    // usuario mexer na configuracao errada do RDS. So a frase conhecida vira a marca.
    erro = ePiDesabilitado(o.message) ? PI_DESABILITADO : (typeof o.message === 'string' ? o.message : 'indisponivel')
  }

  // `janela_segundos` PRECISA atravessar: e ele que faz o cabecalho dizer "Chamadas (24h)" em
  // vez de "Chamadas". Ficou de fora na primeira versao (TASK-9870) e o defeito nao apareceu em
  // teste nenhum, porque os testes de tela mockam `getSlowQueries` e pulam este normalizador —
  // so a conferencia em producao mostrou o cabecalho sem a janela.
  const janela = typeof o.janela_segundos === 'number' ? o.janela_segundos : undefined

  return {
    items: lista,
    count: contagem,
    ...(janela !== undefined ? { janela_segundos: janela } : {}),
    ...(erro ? { error: erro } : {}),
  }
}

/** Um banco COM CARGA na janela — a lista nunca é "os bancos da instância". */
export interface BancoComCarga {
  nome: string
  avg_load: number
  /** `rdsadmin` e afins: banco interno da AWS. A tela mostra com selo, nunca como padrão. */
  interno: boolean
}

export interface BancosResponse {
  items: BancoComCarga[]
  count: number
  error?: string
}

/**
 * Normaliza a lista de bancos na fronteira.
 *
 * Existe porque em 18/08/2026 um campo novo (`janela_segundos`) foi **descartado** por um
 * normalizador que montava o objeto campo por campo, e nenhum teste pegou — os de tela mockam a
 * função da API e pulam este trecho. Aqui o campo desconhecido não passa, mas o conhecido passa
 * com tipo verificado.
 */
export function normalizarBancos(bruto: unknown): BancosResponse {
  const o = (bruto ?? {}) as Record<string, unknown>
  const lista = (Array.isArray(o.items) ? o.items : []) as Record<string, unknown>[]
  const items: BancoComCarga[] = lista
    .filter((b) => typeof b?.nome === 'string' && b.nome)
    .map((b) => ({
      nome: String(b.nome),
      avg_load: typeof b.avg_load === 'number' ? b.avg_load : 0,
      interno: b.interno === true,
    }))
  return {
    items,
    count: typeof o.count === 'number' ? o.count : items.length,
    ...(typeof o.error === 'string' ? { error: o.error } : {}),
  }
}

export interface PIDataPoint {
  ts: string
  value: number
}

export interface PIMetricsResponse {
  metric: string
  points: PIDataPoint[]
  error?: string
}

/**
 * Por que a leitura do Performance Insights falhou — e cada resposta leva a uma acao diferente.
 *
 * O `error` sozinho nao distingue: a AWS devolve `NotAuthorizedException` tanto quando o PI esta
 * desligado na instancia quanto quando falta a permissao `pi:*` no papel IAM, e o backend traduz os
 * dois como "Performance Insights nao habilitado". Mandar habilitar o PI quando o problema e
 * permissao e mandar o usuario mexer no lugar errado.
 *
 * O desempate vem de `PerformanceInsightsEnabled`, da propria descricao da instancia.
 */
export interface JanelaPi {
  ativo: boolean
  expira_em: string | null
  gmud_id?: number | null
  /** Falso quando a varredura que desliga nao esta viva — e ai nao se deve nem oferecer ligar. */
  pode_ativar: boolean
}

export type CausaDeIndisponibilidade = 'desligado' | 'sem-permissao' | 'indeterminado'

export function causaDeIndisponibilidade(
  piHabilitado: boolean | null | undefined,
  erro: string | undefined,
): CausaDeIndisponibilidade | null {
  if (!erro) return null
  if (piHabilitado === false) return 'desligado'
  if (piHabilitado === true) return 'sem-permissao'
  // Nao se sabe o estado. Nao afirmar nem uma coisa nem outra.
  return 'indeterminado'
}

/** Mesmo problema do slow-queries, com os campos `data` (em vez de `points`). */
export function normalizarPI(bruto: unknown): PIMetricsResponse {
  const o = (bruto ?? {}) as Record<string, unknown>
  if (typeof bruto !== 'object' || bruto === null) return { metric: '', points: [] }

  const pontos = (Array.isArray(o.points) ? o.points : Array.isArray(o.data) ? o.data : []) as PIDataPoint[]

  let erro = typeof o.error === 'string' ? o.error : undefined
  if (!erro && o.available === false) {
    erro = ePiDesabilitado(o.message) ? PI_DESABILITADO : (typeof o.message === 'string' ? o.message : 'indisponivel')
  }

  return { metric: typeof o.metric === 'string' ? o.metric : '', points: pontos, ...(erro ? { error: erro } : {}) }
}

// --- API Client ---

const base = (tenantId: number, dbIdentifier: string) =>
  `/v1/tenants/${tenantId}/rds/${dbIdentifier}`

export const databaseApi = {
  listInstances: (tenantId: number) =>
    api.get<RdsInstanceListResponse>(`/v1/tenants/${tenantId}/rds`).then((r) => r.data),

  getInstanceMetrics: (tenantId: number, dbIdentifier: string) =>
    api
      .get<RdsMetricsResponse>(`${base(tenantId, dbIdentifier)}/metrics`)
      .then((r) => r.data),

  getInstanceStatus: (tenantId: number, dbIdentifier: string) =>
    api
      .get<RdsInstanceStatus>(`${base(tenantId, dbIdentifier)}/status`)
      .then((r) => r.data),

  listRecommendations: (
    tenantId: number,
    dbIdentifier: string,
    params?: { status?: string; type?: string; severity?: string; limit?: number },
  ) =>
    api
      .get<DbRecommendationListResponse>(`${base(tenantId, dbIdentifier)}/recommendations`, {
        params,
      })
      .then((r) => r.data),

  getRecommendation: (tenantId: number, dbIdentifier: string, recId: number) =>
    api
      .get<DbRecommendation>(`${base(tenantId, dbIdentifier)}/recommendations/${recId}`)
      .then((r) => r.data),

  applyRecommendation: (tenantId: number, dbIdentifier: string, recId: number) =>
    api
      .post<ApplyRecommendationResponse>(
        `${base(tenantId, dbIdentifier)}/recommendations/${recId}/apply`,
      )
      .then((r) => r.data),

  dismissRecommendation: (tenantId: number, dbIdentifier: string, recId: number, reason: string) =>
    api
      .post<{ status: 'dismissed' }>(
        `${base(tenantId, dbIdentifier)}/recommendations/${recId}/dismiss`,
        { reason },
      )
      .then((r) => r.data),

  getCapacityForecast: (tenantId: number, dbIdentifier: string) =>
    api
      .get<CapacityForecast>(`${base(tenantId, dbIdentifier)}/capacity-forecast`)
      .then((r) => r.data),

  getMetricsHistory: (tenantId: number, dbIdentifier: string, metric: string, days?: number) =>
    api
      .get<MetricsHistoryResponse>(`${base(tenantId, dbIdentifier)}/metrics/history`, {
        params: { metric_name: metric, days },
      })
      .then((r) => r.data),

  getSlowQueries: (
    tenantId: number,
    dbIdentifier: string,
    limit = 25,
    hours = 24,
    database?: string | null,
  ) =>
    api
      .get(`${base(tenantId, dbIdentifier)}/slow-queries`, {
        // 25 e o teto do Performance Insights (26 e recusado pela AWS, medido) — pedir o teto
        // deixa ordenacao e filtro valerem sobre o maior conjunto possivel.
        params: { limit, hours, ...(database ? { database } : {}) },
      })
      .then((r) => normalizarSlowQueries(r.data)),

    /**
   * O texto INTEIRO de uma query, sob demanda.
   *
   * Nao e `useQuery` em nenhum lugar de proposito: e `useMutation`, sem cache — o texto vem do
   * grupo `db.sql`, que **nao e normalizado** e pode trazer literal de cliente. Sem cache, o
   * texto morre ao fechar o modal, e reabrir exige outro clique, que gera outra linha de
   * auditoria. A `hours` tem de ser a da LISTAGEM que produziu a linha: o `Filter` do backend
   * procura a chave naquela janela.
   */
  getQueryStatement: (
    tenantId: number,
    dbIdentifier: string,
    queryId: string,
    hours = 24,
  ) =>
    api
      .get<QueryStatement>(
        `${base(tenantId, dbIdentifier)}/slow-queries/${encodeURIComponent(queryId)}/statement`,
        { params: { hours } },
      )
      .then((r) => r.data),

  /** Bancos COM CARGA na janela. Banco ausente esta ocioso ou nao existe na instancia. */
  getBancosComCarga: (tenantId: number, dbIdentifier: string, hours = 24) =>
    api
      .get(`${base(tenantId, dbIdentifier)}/performance-insights/databases`, {
        params: { hours },
      })
      .then((r) => normalizarBancos(r.data)),

  /** Estado da janela: se esta ativo, ate quando, e se DA para ativar (varredura viva). */
  getJanelaPi: (tenantId: number, dbIdentifier: string) =>
    api
      .get<JanelaPi>(`${base(tenantId, dbIdentifier)}/performance-insights/janela`)
      .then((r) => r.data),

  ativarPi: (tenantId: number, dbIdentifier: string, opcoes?: { gmudId?: number }) =>
    api
      .post(`${base(tenantId, dbIdentifier)}/performance-insights/ativar`, null, {
        params: opcoes?.gmudId ? { gmud_id: opcoes.gmudId } : undefined,
      })
      .then((r) => r.data),

  desativarPi: (tenantId: number, dbIdentifier: string) =>
    api
      .delete(`${base(tenantId, dbIdentifier)}/performance-insights/ativar`)
      .then((r) => r.data),

  getPerformanceInsights: (
    tenantId: number,
    dbIdentifier: string,
    hours = 24,
    database?: string | null,
  ) =>
    api
      .get(`${base(tenantId, dbIdentifier)}/performance-insights`, {
        // O grafico acompanha o banco da tabela: duas escalas na mesma tela obrigam quem le a
        // adivinhar de qual banco e cada numero.
        params: { hours, ...(database ? { database } : {}) },
      })
      .then((r) => normalizarPI(r.data)),
}
